import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';
import instance from 'services/api';
import {
  INCLUDE_SECTIONS,
  INCLUDE_SECTION_VALUES,
  EXCLUDED_SUMMARY_REPORT_SECTIONS,
  EXCLUDED_DELIVERY_REPORT_SECTIONS,
  getTabIncludeSections,
  getTabIncludeValues,
  mergeIncludeGroupSelection,
} from 'components/Reporting/shared/schema';

const POS_PERFORMANCE_OPTIONS = getTabIncludeSections(
  EXCLUDED_SUMMARY_REPORT_SECTIONS
);
const POS_PERFORMANCE_VALUES = getTabIncludeValues(
  EXCLUDED_SUMMARY_REPORT_SECTIONS
);
const DELIVERY_REPORT_OPTIONS = getTabIncludeSections(
  EXCLUDED_DELIVERY_REPORT_SECTIONS
);
const DELIVERY_REPORT_VALUES = getTabIncludeValues(
  EXCLUDED_DELIVERY_REPORT_SECTIONS
);
const POS_REPORT_VALUES = INCLUDE_SECTION_VALUES;

const BATCH_EXCLUDED = [
  'totalProfitReport',
  'employeeTimesheetReport',
  'detailedReport',
];
const BATCH_REPORT_OPTIONS = INCLUDE_SECTIONS.filter(
  (s) => !BATCH_EXCLUDED.includes(s.value)
);
const BATCH_REPORT_VALUES = INCLUDE_SECTION_VALUES.filter(
  (v) => !BATCH_EXCLUDED.includes(v)
);

const normalizeSelection = (values, allValues) =>
  allValues.filter((value) => values.includes(value));

const areSelectionsEqual = (left, right, allValues) => {
  const normalizedLeft = normalizeSelection(left, allValues);
  const normalizedRight = normalizeSelection(right, allValues);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const ConfigPanel = ({
  title,
  options,
  allValues,
  defaultValues,
  selected,
  onChange,
  onSave,
  saving,
  saveDisabled,
}) => {
  const selectedSet = new Set(selected);
  const allSelected = selectedSet.size === allValues.length;

  const handleToggle = (value) => {
    const next = new Set(selectedSet);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(Array.from(next));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={allSelected}
          onClick={() => onChange([...allValues])}
        >
          All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onChange([...defaultValues])}
        >
          Default
        </Button>
      </Box>
      <FormGroup>
        <Grid container>
          {options.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.value}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedSet.has(item.value)}
                    onChange={() => handleToggle(item.value)}
                  />
                }
                label={item.label}
              />
            </Grid>
          ))}
        </Grid>
      </FormGroup>
      <Box sx={{ mt: 2 }}>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={saveDisabled}
          onClick={onSave}
        >
          Save
        </LoadingButton>
      </Box>
    </Box>
  );
};

const ReportConfigSection = () => {
  const [loading, setLoading] = useState(true);
  const [posInclude, setPosInclude] = useState([]);
  const [posDefaults, setPosDefaults] = useState([...POS_REPORT_VALUES]);
  const [savedPosInclude, setSavedPosInclude] = useState([...POS_REPORT_VALUES]);
  const [batchInclude, setBatchInclude] = useState([]);
  const [batchDefaults, setBatchDefaults] = useState([...BATCH_REPORT_VALUES]);
  const [savedBatchInclude, setSavedBatchInclude] = useState([
    ...BATCH_REPORT_VALUES,
  ]);
  const [savingPos, setSavingPos] = useState(false);
  const [savingBatch, setSavingBatch] = useState(false);

  useEffect(() => {
    instance
      .get('/business')
      .then(({ data: { data } }) => {
        const savedPos = data?.settings?.posReportConfig?.include;
        const posValues = normalizeSelection(
          savedPos ?? [...POS_REPORT_VALUES],
          POS_REPORT_VALUES
        );
        setPosInclude(posValues);
        setPosDefaults(posValues);
        setSavedPosInclude(posValues);
        const savedBatch = data?.settings?.batchReportConfig?.include;
        const batchValues = normalizeSelection(
          savedBatch ?? [...BATCH_REPORT_VALUES],
          BATCH_REPORT_VALUES
        );
        setBatchInclude(batchValues);
        setBatchDefaults(batchValues);
        setSavedBatchInclude(batchValues);
      })
      .catch(() => {
        toast.error('Failed to load report configuration');
      })
      .finally(() => setLoading(false));
  }, []);

  const isPosDirty = useMemo(
    () => !areSelectionsEqual(posInclude, savedPosInclude, POS_REPORT_VALUES),
    [posInclude, savedPosInclude]
  );

  const isBatchDirty = useMemo(
    () =>
      !areSelectionsEqual(batchInclude, savedBatchInclude, BATCH_REPORT_VALUES),
    [batchInclude, savedBatchInclude]
  );

  const handleSavePos = useCallback(() => {
    if (!isPosDirty) {
      return;
    }

    setSavingPos(true);
    instance
      .patch('/business/pos-report-config', { include: posInclude })
      .then(({ data }) => {
        const nextSavedSelection = normalizeSelection(posInclude, POS_REPORT_VALUES);
        setSavedPosInclude(nextSavedSelection);
        setPosDefaults(nextSavedSelection);

        if (!data?.message) {
          toast.success('POS report config successfully updated');
        }
      })
      .catch((err) => {
        if (!err?.response?.data?.message) {
          toast.error('Failed to update POS report config');
        }
      })
      .finally(() => setSavingPos(false));
  }, [isPosDirty, posInclude]);

  const handleSaveBatch = useCallback(() => {
    if (!isBatchDirty) {
      return;
    }

    setSavingBatch(true);
    instance
      .patch('/business/batch-report-config', { include: batchInclude })
      .then(({ data }) => {
        const nextSavedSelection = normalizeSelection(
          batchInclude,
          BATCH_REPORT_VALUES
        );
        setSavedBatchInclude(nextSavedSelection);
        setBatchDefaults(nextSavedSelection);

        if (!data?.message) {
          toast.success('Batch report config successfully updated');
        }
      })
      .catch((err) => {
        if (!err?.response?.data?.message) {
          toast.error('Failed to update Batch report config');
        }
      })
      .finally(() => setSavingBatch(false));
  }, [batchInclude, isBatchDirty]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <ConfigPanel
        title="POS Performance Report"
        options={POS_PERFORMANCE_OPTIONS}
        allValues={POS_PERFORMANCE_VALUES}
        defaultValues={posDefaults.filter((value) =>
          POS_PERFORMANCE_VALUES.includes(value)
        )}
        selected={posInclude.filter((value) =>
          POS_PERFORMANCE_VALUES.includes(value)
        )}
        onChange={(nextGroup) =>
          setPosInclude((prev) =>
            mergeIncludeGroupSelection(
              prev,
              POS_PERFORMANCE_VALUES,
              nextGroup
            )
          )
        }
        onSave={handleSavePos}
        saving={savingPos}
        saveDisabled={!isPosDirty}
      />
      <ConfigPanel
        title="Delivery Report"
        options={DELIVERY_REPORT_OPTIONS}
        allValues={DELIVERY_REPORT_VALUES}
        defaultValues={posDefaults.filter((value) =>
          DELIVERY_REPORT_VALUES.includes(value)
        )}
        selected={posInclude.filter((value) =>
          DELIVERY_REPORT_VALUES.includes(value)
        )}
        onChange={(nextGroup) =>
          setPosInclude((prev) =>
            mergeIncludeGroupSelection(prev, DELIVERY_REPORT_VALUES, nextGroup)
          )
        }
        onSave={handleSavePos}
        saving={savingPos}
        saveDisabled={!isPosDirty}
      />
      <ConfigPanel
        title="Batch Report Config"
        options={BATCH_REPORT_OPTIONS}
        allValues={BATCH_REPORT_VALUES}
        defaultValues={batchDefaults}
        selected={batchInclude}
        onChange={setBatchInclude}
        onSave={handleSaveBatch}
        saving={savingBatch}
        saveDisabled={!isBatchDirty}
      />
    </Box>
  );
};

export default ReportConfigSection;
